import { useState } from 'react';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { IGetBookingQuery } from '../../../services/bookingService/__generated__/queries.generated';
import { useGetProtectionPlansByUserIdLazyQuery } from '../../../services/protectionPlanService/__generated__/queries.generated';
import { IProtectionPlan, ProtectionPlanEnum } from '../../../../__generated__/graphql';
import { PlanRadioList } from '../planRadioList';
import { Modal } from '../../modal';
import { MoonLoader } from 'react-spinners';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Colors } from '../../../styles/colors';
import { formatProtectionOutOfPocketMax } from '../../../utils/declinedProtectionLiability';

const ProtectionPlanDetailsContainer = styled.div`
  ${tw`mt-2 border rounded-lg box-border flex items-center mb-2`}
`;
const ProtectionPlanDetailsTitle = styled.div`
  ${tw`text-lg text-text-secondary`}
`;
const ProtectionPlanDetailsButton = styled.a`
  ${tw`underline text-text-secondary text-lg cursor-pointer pl-2 hover:text-text-primary`}
`;
const ViewDetailsButton = styled.button`
  ${tw`text-sm underline bg-transparent border-none cursor-pointer pl-2`}
  color: ${Colors.PrimaryBlueDark};

  &:hover {
    color: ${Colors.Blue700};
  }
`;

const ModalTitle = styled.h3`
  ${tw`text-lg font-semibold text-text-primary mb-4`}
`;

const DetailRow = styled.div`
  ${tw`mb-3`}
`;

const DetailLabel = styled.div`
  ${tw`text-xs text-text-secondary`}
`;

const DetailValue = styled.div`
  ${tw`text-sm text-text-primary font-medium`}
`;

const DetailHelper = styled.div`
  ${tw`text-xs text-text-secondary mt-0.5`}
`;

const CoverageTitle = styled.h4`
  ${tw`text-sm font-semibold text-text-primary mt-4 mb-2`}
`;

const CoverageBody = styled.div`
  ${tw`text-sm text-text-secondary prose prose-sm max-w-none`}
`;

interface ProtectionPlanDetailsProps {
  booking: IGetBookingQuery['booking'];
}

/** Re-enable when post-booking protection upgrade is ready to ship. */
const PROTECTION_PLAN_UPGRADE_ENABLED = false;

type PurchasedProtectionPlanView = {
  planType: string;
  planName: string;
  dailyPrice: number | null;
  outOfPocket: number | null;
  declinedProtectionOutOfPocketPercent: number | null;
  declinedProtectionOutOfPocketAmt: string | null;
  description: string | null;
  policyDetails: string | null;
  isMonthly: boolean;
  isDeclined: boolean;
};

const formatPlanDisplayName = (plan: string): string =>
  plan
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

const resolvePurchasedProtectionPlan = (
  booking: IGetBookingQuery['booking'],
): PurchasedProtectionPlanView | null => {
  const snapshot = booking.protectionPlanSnapshot;
  const entity = booking.protectionPlanEntity;
  const planType = snapshot?.planType ?? entity?.plan ?? booking.protectionPlan;
  if (!planType) {
    return null;
  }

  const isDeclined = planType === ProtectionPlanEnum.Declined;
  const planName =
    snapshot?.planName?.trim() || (planType ? formatPlanDisplayName(planType) : 'Unknown');

  return {
    planType,
    planName,
    dailyPrice: isDeclined ? null : (snapshot?.dailyPrice ?? entity?.dailyPrice ?? null),
    outOfPocket: isDeclined ? null : (snapshot?.outOfPocket ?? entity?.outOfPocket ?? null),
    declinedProtectionOutOfPocketPercent:
      (snapshot as any)?.declinedProtectionOutOfPocketPercent ??
      (entity as any)?.declinedProtectionOutOfPocketPercent ??
      null,
    declinedProtectionOutOfPocketAmt:
      (snapshot as any)?.declinedProtectionOutOfPocketAmt ??
      (entity as any)?.declinedProtectionOutOfPocketAmt ??
      null,
    description: snapshot?.description ?? entity?.description ?? null,
    policyDetails: snapshot?.policyDetails ?? entity?.policyDetails ?? null,
    isMonthly: planType.startsWith('MONTHLY_'),
    isDeclined,
  };
};

export const ProtectionPlanDetails = ({ booking }: ProtectionPlanDetailsProps) => {
  const [isUpgradePlanModalOpen, setIsUpgradePlanModalOpen] = useState(false);
  const [isViewDetailsModalOpen, setIsViewDetailsModalOpen] = useState(false);
  const [selected, setSelected] = useState<IProtectionPlan | undefined | null>(
    booking.protectionPlanEntity as IProtectionPlan,
  );

  const purchasedPlan = resolvePurchasedProtectionPlan(booking);
  const selectedPlan = booking.protectionPlanEntity?.plan ?? booking.protectionPlan ?? null;

  const [
    getProtectionPlanByUserId,
    { data: protectionPlanData, loading: getProtectionPlansLoading },
  ] = useGetProtectionPlansByUserIdLazyQuery({
    onCompleted: () => {
      setIsUpgradePlanModalOpen(true);
    },
    onError: () => {
      toast.error('Failed to get protection plans');
    },
  });

  const onUpgradePlanClicked = () => {
    getProtectionPlanByUserId({
      variables: {
        userId: booking.host?.id ?? '',
      },
    });
  };

  const onCloseUpgradePlanModal = () => {
    setIsUpgradePlanModalOpen(false);
  };

  const renderPlans = () => {
    if (!protectionPlanData) return <div>no plans</div>;
    const plans = protectionPlanData.protectionPlansByUserId;
    return (
      <PlanRadioList
        plans={plans as IProtectionPlan[]}
        selected={selected as IProtectionPlan}
        setSelected={setSelected}
        setShowDeclineDialog={() => {}}
        showDeclineButton={false}
      />
    );
  };

  const renderPurchasedPlanDetails = () => {
    if (!purchasedPlan) return null;

    const receipt = booking.receipt;
    const insuranceByTrip = receipt?.insuranceByTrip;
    const totalDays = receipt?.totalDays;
    const rateSuffix = purchasedPlan.isMonthly ? '/month' : '/day';
    const coverageSource =
      purchasedPlan.policyDetails?.trim() || purchasedPlan.description?.trim() || '';

    return (
      <>
        <ModalTitle>{purchasedPlan.planName} protection</ModalTitle>
        <DetailRow>
          <DetailLabel>Plan</DetailLabel>
          <DetailValue>{purchasedPlan.planName}</DetailValue>
        </DetailRow>
        {purchasedPlan.dailyPrice != null && (
          <DetailRow>
            <DetailLabel>Rate</DetailLabel>
            <DetailValue>
              ${purchasedPlan.dailyPrice.toFixed(2)}
              {rateSuffix}
            </DetailValue>
          </DetailRow>
        )}
        {insuranceByTrip != null && (
          <DetailRow>
            <DetailLabel>Trip protection</DetailLabel>
            <DetailValue>${insuranceByTrip.toFixed(2)}</DetailValue>
            <DetailHelper>
              {purchasedPlan.isMonthly
                ? 'Total protection for this trip (tax included)'
                : totalDays != null && purchasedPlan.dailyPrice != null
                  ? `${totalDays} days @ $${purchasedPlan.dailyPrice.toFixed(2)}/day (tax included)`
                  : 'Total protection for this trip (tax included)'}
            </DetailHelper>
          </DetailRow>
        )}
        {(purchasedPlan.isDeclined || purchasedPlan.outOfPocket != null) && (
          <DetailRow>
            <DetailLabel>Out-of-pocket max</DetailLabel>
            <DetailValue>
              {formatProtectionOutOfPocketMax({
                isDeclined: purchasedPlan.isDeclined,
                outOfPocket: purchasedPlan.outOfPocket,
                declinedProtectionOutOfPocketPercent:
                  purchasedPlan.declinedProtectionOutOfPocketPercent,
                declinedProtectionOutOfPocketAmt: purchasedPlan.declinedProtectionOutOfPocketAmt,
              })}
            </DetailValue>
          </DetailRow>
        )}
        <CoverageTitle>Coverage details</CoverageTitle>
        {coverageSource ? (
          <CoverageBody>
            <ReactMarkdown rehypePlugins={[rehypeRaw]}>{coverageSource}</ReactMarkdown>
          </CoverageBody>
        ) : (
          <DetailHelper>No additional details available.</DetailHelper>
        )}
      </>
    );
  };

  const coverageLabel = purchasedPlan?.planName ?? selectedPlan ?? 'declined';

  return (
    <ProtectionPlanDetailsContainer>
      <ProtectionPlanDetailsTitle>{coverageLabel} coverage</ProtectionPlanDetailsTitle>

      {PROTECTION_PLAN_UPGRADE_ENABLED &&
        (!selectedPlan || selectedPlan === ProtectionPlanEnum.Declined) && (
          <ProtectionPlanDetailsButton onClick={onUpgradePlanClicked}>
            upgrade plan
          </ProtectionPlanDetailsButton>
        )}
      {purchasedPlan && (
        <ViewDetailsButton type="button" onClick={() => setIsViewDetailsModalOpen(true)}>
          View protection plan
        </ViewDetailsButton>
      )}
      {PROTECTION_PLAN_UPGRADE_ENABLED && getProtectionPlansLoading && <MoonLoader />}
      {PROTECTION_PLAN_UPGRADE_ENABLED && (
        <Modal isOpen={isUpgradePlanModalOpen} onClose={onCloseUpgradePlanModal}>
          {renderPlans()}
        </Modal>
      )}
      <Modal isOpen={isViewDetailsModalOpen} onClose={() => setIsViewDetailsModalOpen(false)}>
        {renderPurchasedPlanDetails()}
      </Modal>
    </ProtectionPlanDetailsContainer>
  );
};
